import { extractDocxText } from "./document-formats/docx";
import { extractEpubText } from "./document-formats/epub";
import type {
  DocumentExtraction,
  ExtractedDocumentSection,
} from "./document-formats/types";

export const MAX_DOCUMENT_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_EPUB_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_EXTRACTED_DOCUMENT_CHARACTERS = 1_000_000;
const MIN_SCANNER_CHARACTERS = 200;
const MAX_SCANNER_CHARACTERS = 10_000;

export interface ImportedDocument {
  fileName: string;
  format: string;
  text: string;
  warnings: string[];
  sections: ExtractedDocumentSection[];
  initialSectionId: string;
  totalCharacters: number;
  description: string;
}

interface DocumentFormatAdapter {
  format: string;
  extensions: string[];
  mimeTypes: string[];
  maxFileBytes: number;
  description: string;
  unreadableReason: string;
  extract(file: File): Promise<DocumentExtraction>;
}

const documentFormatAdapters: DocumentFormatAdapter[] = [
  {
    format: "DOCX",
    extensions: [".docx"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    maxFileBytes: MAX_DOCUMENT_FILE_BYTES,
    description:
      "Imported as plain text. Images, comments, and document styling are not included.",
    unreadableReason: "It may be damaged or password-protected.",
    extract: extractDocxText,
  },
  {
    format: "EPUB",
    extensions: [".epub"],
    mimeTypes: ["application/epub+zip"],
    maxFileBytes: MAX_EPUB_FILE_BYTES,
    description:
      "Imported in book reading order. Images, navigation, scripts, and styling are not included.",
    unreadableReason: "It may be damaged or protected by DRM.",
    extract: extractEpubText,
  },
];

export const DOCUMENT_FILE_ACCEPT = documentFormatAdapters
  .flatMap((adapter) => [...adapter.extensions, ...adapter.mimeTypes])
  .join(",");

export class DocumentImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DocumentImportError";
  }
}

function fileSizeInMegabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  });
}

export async function importDocument(file: File): Promise<ImportedDocument> {
  const lowerName = file.name.toLowerCase();
  const adapter = documentFormatAdapters.find((candidate) =>
    candidate.extensions.some((extension) => lowerName.endsWith(extension)),
  );
  if (!adapter) {
    throw new DocumentImportError(
      "Choose a .docx or .epub file. Older .doc files are not supported.",
    );
  }
  if (file.size > adapter.maxFileBytes) {
    throw new DocumentImportError(
      `${adapter.format} files must be ${fileSizeInMegabytes(adapter.maxFileBytes)} MB or smaller. This file is ${fileSizeInMegabytes(file.size)} MB.`,
    );
  }

  let extracted: DocumentExtraction;
  try {
    extracted = await adapter.extract(file);
  } catch {
    throw new DocumentImportError(
      `Stylome could not read this ${adapter.format} file. ${adapter.unreadableReason}`,
    );
  }
  if (!extracted.text) {
    throw new DocumentImportError(
      `This ${adapter.format} file does not contain readable text.`,
    );
  }
  if (extracted.text.length > MAX_EXTRACTED_DOCUMENT_CHARACTERS) {
    throw new DocumentImportError(
      `This ${adapter.format} file contains more than 1,000,000 characters and is too large to import.`,
    );
  }
  const sections = extracted.sections?.length
    ? extracted.sections
    : [{ id: "document", label: adapter.format, text: extracted.text }];
  const initialSection =
    sections.find(
      (section) =>
        section.text.length >= MIN_SCANNER_CHARACTERS &&
        section.text.length <= MAX_SCANNER_CHARACTERS,
    ) ||
    sections.find((section) => section.text.length >= MIN_SCANNER_CHARACTERS) ||
    sections[0];
  return {
    fileName: file.name,
    format: adapter.format,
    text: initialSection.text,
    warnings: extracted.warnings,
    sections,
    initialSectionId: initialSection.id,
    totalCharacters: extracted.text.length,
    description: adapter.description,
  };
}
